import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const PATH = "/heritage/kunskapsarv";

const USE_CASES = [
  "Kunskapsöverföring inför pension",
  "Intervju med grundare",
  "Dokumentation av företagskultur",
  "Onboardingmaterial",
  "Intern utbildning",
  "Generationsskifte",
  "Företagshistoria",
  "Dokumentation av arbetssätt och erfarenhet",
];

const PROCESS = [
  { label: "Första samtal", body: "Vi förstår syftet, vilken kunskap som ska dokumenteras och hur materialet ska användas internt." },
  { label: "Intervjuupplägg", body: "Frågeområden och struktur tas fram tillsammans med er, anpassat efter kunskapsområden och målgrupp." },
  { label: "Filmad intervju", body: "En eller flera intervjuer spelas in på kontoret, i en studio eller i den miljö där arbetet utförs." },
  { label: "Klippning och kapitelindelning", body: "Materialet klipps till en huvudfilm och kortare delar per kunskapsområde — användbart i onboarding och utbildning." },
  { label: "Leverans", body: "Filmer levereras digitalt för intern användning. Vi kan också paketera material för intranät eller LMS." },
];

const PACKAGES = [
  {
    name: "Key Person Interview",
    body:
      "För en senior medarbetare, specialist, ledare eller grundare. Innehåller förmöte, intervjuupplägg, filmad intervju, klippning och intern leverans.",
  },
  {
    name: "Knowledge Transfer",
    body:
      "För organisationer som behöver dokumentera tyst kunskap, arbetssätt och erfarenhet. Intervjustruktur, kunskapsområden, filmad intervju eller intervjuserie och kapitelindelat material.",
  },
  {
    name: "Company Legacy",
    body:
      "För företag som vill dokumentera historia, kultur, grundarresa eller generationsskifte. Intervjuer med flera personer, sammanfattande film och kortare interna klipp.",
  },
];

const VALUE = [
  "Minskar risken att viktig erfarenhet försvinner när personer lämnar organisationen",
  "Underlättar onboarding och intern utbildning",
  "Dokumenterar kultur, värderingar och arbetssätt",
  "Bevarar grundarhistoria och företagsminne",
  "Kan användas vid generationsskiften, pensionsavgångar och jubileum",
  "Gör tyst kunskap mer tillgänglig för fler i organisationen",
];

const FAQ = [
  {
    q: "Vad är kunskapsarv?",
    a: "Kunskapsarv är strukturerad filmad dokumentation av erfarenhet, arbetssätt och tyst kunskap i en organisation. Materialet används för onboarding, internutbildning och som bevarat företagsminne.",
  },
  {
    q: "Hur dokumenterar man tyst kunskap?",
    a: "Genom strukturerade intervjuer där rätt frågor öppnar för konkreta exempel, beslutslogik och erfarenhet. Filmat material bevarar både innehåll och kontext — kroppsspråk, tonläge och berättarstil.",
  },
  {
    q: "Hur överför man kunskap när någon går i pension?",
    a: "Genom att intervjua personen i god tid före avgång, dokumentera arbetssätt, beslutsmodeller och nyckelerfarenheter, och klippa materialet i kapitel som efterträdare och nya medarbetare kan ta del av.",
  },
  {
    q: "Vilka roller passar för intervjuerna?",
    a: "Grundare, seniora specialister, ledare, projektansvariga, personer med lång erfarenhet i kärnverksamheten — eller hela team som tillsammans bär en arbetsmetod.",
  },
  {
    q: "Hur används materialet internt?",
    a: "Vanligaste användningarna är onboarding av nya medarbetare, intern utbildning, intranät, kulturarbete, jubileum och som referens vid generationsskiften.",
  },
  {
    q: "Vad kostar Knowledge Transfer?",
    a: "Pris beror på antal personer, omfattning och hur mycket material som ska klippas. Vi lämnar offert efter ett första samtal där vi förstår behovet.",
  },
];

export const Route = createFileRoute("/heritage/kunskapsarv")({
  head: () => ({
    meta: [
      { title: "Kunskapsarv — tyst kunskap på film | Fenny" },
      {
        name: "description",
        content:
          "Filmad dokumentation av erfarenhet, arbetssätt och tyst kunskap för företag och organisationer. Onboarding, generationsskifte, grundarhistoria och företagsminne.",
      },
      { property: "og:title", content: "Kunskapsarv på film — Fenny Heritage" },
      {
        property: "og:description",
        content:
          "Strukturerade filmade intervjuer som bevarar erfarenhet och tyst kunskap i organisationen.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [
      { rel: "canonical", href: `https://fenny.se${PATH}` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/heritage/kunskapsarv", name: "Kunskapsarv — tyst kunskap på film | Fenny", description: "Filmad dokumentation av erfarenhet, arbetssätt och tyst kunskap för företag och organisationer. Onboarding, generationsskifte, grundarhistoria och företagsminne."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Kunskapsarv på film",
            path: PATH,
            description:
              "Fenny Heritage producerar strukturerade filmade intervjuer för företag och organisationer som vill dokumentera erfarenhet, arbetssätt, tyst kunskap och företagshistoria.",
            serviceType: "Filmad kunskapsöverföring och företagsdokumentation",
            priceRange: "Offert efter första samtal",
            areaServed: ["Sweden"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Heritage", path: "/heritage" },
            { name: "Kunskapsarv", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: KunskapsarvPage,
});

function KunskapsarvPage() {
  return (
    <article className="pt-28">
      <Section className="pb-10">
        <div className="max-w-4xl">
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">
            Heritage Business
          </div>
          <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
            Kunskapsarv — dokumentera erfarenhet och tyst kunskap
          </h1>
          <p className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl" data-speakable>
            Strukturerade filmade intervjuer med nyckelpersoner, grundare och
            seniora medarbetare. Materialet används för onboarding, internutbildning,
            kulturarbete, jubileum och dokumentation.
          </p>
          <QuickAnswer
            className="mt-6"
            text="Kunskapsarv är filmad dokumentation av erfarenhet, arbetssätt och tyst kunskap i en organisation — bevarat och paketerat för onboarding, internutbildning och som företagsminne."
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-3 text-sm font-medium text-background hover:opacity-90"
            >
              Boka ett första samtal <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/heritage"
              className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-3 text-sm font-medium hover:bg-foreground/[0.05]"
            >
              Tillbaka till Heritage
            </Link>
          </div>
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Värde för organisationen</h2>
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {VALUE.map((v) => (
            <li key={v} className="flex gap-3 rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-foreground/85">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-mint" />
              <span>{v}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Exempel på användning</h2>
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {USE_CASES.map((u) => (
            <li key={u} className="rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-foreground/85">
              {u}
            </li>
          ))}
        </ul>
      </Section>

      <Section className="py-10" id="process">
        <h2 className="text-3xl font-semibold md:text-4xl">Så går det till</h2>
        <ol className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {PROCESS.map((s, i) => (
            <li key={s.label} className="rounded-2xl border border-white/10 bg-surface/40 p-5">
              <div className="text-xs uppercase tracking-widest text-mint">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-2 font-medium">{s.label}</div>
              <p className="mt-2 text-sm text-foreground/75">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section className="py-10" id="paket">
        <h2 className="text-3xl font-semibold md:text-4xl">Paket</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {PACKAGES.map((p) => (
            <div key={p.name} className="rounded-2xl border border-white/10 bg-surface/40 p-6">
              <div className="font-medium">{p.name}</div>
              <p className="mt-3 text-sm text-foreground/75">{p.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-foreground/65">
          Priser lämnas som offert efter första samtalet.
        </p>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Vanliga frågor</h2>
        <div className="mt-8 max-w-3xl space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/75">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <RelatedLinksRail sourcePath={PATH} />
      <SignatureCta />
    </article>
  );
}
